import type { ChartData } from 'chart.js'
import type { AggregatedWorkoutData, MultiMachineIntervalResult, PerformanceMetrics, TimeRangeFilter, WeightUnitsFilter, Workout } from './WorkoutTypes'

export const CUSTOM_DEVICE_MAP: Record<string, { label: string, color: string }> = {
  'bike'    : { label: 'Echo Bike', color: '#BC0000' },
  'rower'   : { label: 'Echo Rower', color: '#499711' },
  'ski'     : { label: 'Echo SKI', color: '#1277b7' },
  // Fallback in case we have data that is missing device info. Shouldn't happen.
  'unknown' : { label: 'Unknown Device', color: '#666666' },
}

/** The Air Rhino has its own stats view; it never mixes into the machines' charts */
export const AIR_RHINO = { label: 'Air Rhino', color: '#1277b7' }
/** The Air Rhino's device type, and its key among a multi-machine record's per-machine slices */
export const RHINO = 'rhino'
/** Slices the app saves for devices that are not cardio machines: the rhino, and a timer that shares a session */
const NON_MACHINE_ROLES = new Set([RHINO, 'timer'])
const isMachineRole = (role: string): boolean => !NON_MACHINE_ROLES.has(role)

export const FEET_PER_METER = 3.28084
export const YARDS_PER_METER = 1.09361
const LB_PER_KG = 2.20462

/**
 * A record the Air Rhino's console wrote, or a multi-machine session it took
 * part in: its weight, weight-seconds and reps belong in the rhino's view.
 */
export function isRhinoWorkout(workout: Workout): boolean {
  const byModality = workout.data.performanceMetrics?.byModality
  if (byModality) return RHINO in byModality

  return workout.data.deviceInfo?.deviceType === RHINO
}

/**
 * A record a cardio machine wrote, or a multi-machine session one took part
 * in: its distance belongs in the machines' view. A session that mixed the
 * Air Rhino with a bike is both, each view reading only its own share.
 */
export function isMachineWorkout(workout: Workout): boolean {
  const byModality = workout.data.performanceMetrics?.byModality
  if (byModality) return Object.keys(byModality).some(isMachineRole)

  return isMachineRole(workout.data.deviceInfo?.deviceType || 'unknown')
}

/** Which of a record's machines a view reads: one of them, the Air Rhino, or every cardio machine ('all') */
function keepsRole(equipment: string): (role: string) => boolean {
  if (equipment === 'all') return isMachineRole

  return role => role === equipment
}

/** How long a record ran by its own clock, start to end */
function wallClockMs(workout: Workout): number {
  if (!workout.data.payload) return 0
  const start = new Date(workout.data.payload.createdAt).getTime()
  const end = new Date(workout.data.payload.endedAt).getTime()

  // Don't display negative values
  return Math.max(end - start, 0)
}

const sum = <T>(items: T[], read: (item: T) => number | undefined): number => items.reduce((total, item) => total + (read(item) || 0), 0)

export type WorkoutShare = {
  /** Metres */
  distance: number,
  calories: number,
  durationMs: number,
}

/**
 * What a record contributes to a view: one cardio machine's share, the Air
 * Rhino's, or every cardio machine's ('all'). A multi-machine session is
 * split by the per-machine slices the app saved, so a bike's metres never
 * count as the rhino's rope travel and its time is each machine's own. A
 * record the view reads whole keeps its totals and its clock.
 */
export function workoutShare(workout: Workout, equipment: string): WorkoutShare {
  const metrics = workout.data.performanceMetrics
  const keeps = keepsRole(equipment)
  const whole: WorkoutShare = { distance: metrics?.totalDistance || 0, calories: metrics?.totalCalories || 0, durationMs: wallClockMs(workout) }
  const nothing: WorkoutShare = { distance: 0, calories: 0, durationMs: 0 }

  if (metrics?.byModality) {
    const roles = Object.keys(metrics.byModality)
    if (roles.every(keeps)) return whole
    const slices = roles.filter(keeps).map(role => metrics.byModality![role])

    return {
      distance   : sum(slices, slice => slice.distance),
      calories   : sum(slices, slice => slice.calories),
      durationMs : sum(slices, slice => slice.duration) * 1000,
    }
  }
  if (workout.data.multiMachineIntervalResults) {
    // DEPRECATED: sessions recorded before the app saved per-machine slices
    const results = Object.values(workout.data.multiMachineIntervalResults).flat()
    if (results.every(result => keeps(result.machineRole))) return whole
    const kept = results.filter(result => keeps(result.machineRole))

    return {
      distance   : sum(kept, result => result.distanceMeters),
      calories   : sum(kept, result => result.calories),
      durationMs : sum(kept, result => result.elapsedSeconds) * 1000,
    }
  }

  return keeps(workout.data.deviceInfo?.deviceType || 'unknown') ? whole : nothing
}

/**
 * A record's distance per cardio machine, in metres, for the machines'
 * charts and tables: the machines the filter keeps, never the Air Rhino's
 * rope travel.
 */
function machineDistances(workout: Workout, equipmentFilter = 'all'): [string, number][] {
  const metrics = workout.data.performanceMetrics
  const keeps = keepsRole(equipmentFilter)

  if (metrics?.byModality) {
    return Object.entries(metrics.byModality)
      .filter(([role]) => keeps(role))
      .map(([role, slice]) => [role, slice.distance || 0])
  }
  if (workout.data.multiMachineIntervalResults) {
    // DEPRECATED: sessions recorded before the app saved per-machine slices
    const meters: Record<string, number> = {}
    Object.values(workout.data.multiMachineIntervalResults).flat()
      .filter((result: MultiMachineIntervalResult) => keeps(result.machineRole))
      .forEach(result => {
        meters[result.machineRole] = (meters[result.machineRole] || 0) + result.distanceMeters
      })

    return Object.entries(meters)
  }
  if (!metrics) return []
  const role = workout.data.deviceInfo?.deviceType || 'unknown'

  return keeps(role) ? [[role, metrics.totalDistance || 0]] : []
}

/** A distance in metres, read in the chosen unit */
function inUnits(meters: number, units: string): number {
  const yards = meters * YARDS_PER_METER

  return units === 'meters'
    ? meters
    : units === 'yards'
      ? yards
      : yards / 1760
}

/** The unit family an Air Rhino record's weight figures were recorded in; the console defaults to KG */
function recordedWeightUnit(metrics: PerformanceMetrics): WeightUnitsFilter['value'] {
  return metrics.totalImpulseUnit === 'lb-s' ? 'lb' : 'kg'
}

function convertWeight(value: number, from: WeightUnitsFilter['value'], to: WeightUnitsFilter['value']): number {
  if (from === to) return value

  return to === 'lb' ? value * LB_PER_KG : value / LB_PER_KG
}

/** Total weight an Air Rhino record moved, in the requested unit */
export function rhinoWeight(metrics: PerformanceMetrics | undefined, unit: WeightUnitsFilter['value']): number {
  if (!metrics?.totalForce) return 0

  return convertWeight(metrics.totalForce, recordedWeightUnit(metrics), unit)
}

/** Total weight-seconds (KG-S / LB-S) an Air Rhino record accumulated, in the requested unit */
export function rhinoImpulse(metrics: PerformanceMetrics | undefined, unit: WeightUnitsFilter['value']): number {
  if (!metrics?.totalImpulse) return 0

  return convertWeight(metrics.totalImpulse, recordedWeightUnit(metrics), unit)
}

const MONTH_ABBREVIATIONS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

const normalizeDate = (date: Date): Date => {
  date.setHours(0, 0, 0, 0)

  return date
}

type TimeGrouping = {
  /** The bar chart's x-axis, in order */
  labels: string[],
  /** The axis label a workout on this date lands under, or null when it falls outside the range */
  getGroupLabel: (date: Date) => string | null,
}

/**
 * How a bar chart buckets workouts for a time range: by day for a week or a
 * short custom range, by week for four weeks, by month for three months, year
 * to date or a longer custom range, and by year for all time.
 */
function timeGrouping(workouts: Workout[], timeRange: TimeRangeFilter['value'], dateRange?: [number, number] | null): TimeGrouping {
  let labels: string[] = []

  let getGroupLabel: (date: Date) => string | null = () => null

  const today = normalizeDate(new Date())
  const currentYear = today.getFullYear()
  const msPerDay = 24 * 60 * 60 * 1000

  switch (timeRange) {
    case 'custom': {
      if (!dateRange || dateRange.length !== 2) {
        break
      }

      const [startDateTimestamp, endDateTimestamp] = dateRange
      const startDate = new Date(startDateTimestamp)
      startDate.setHours(0, 0, 0, 0)
      const endDate = new Date(endDateTimestamp)
      endDate.setHours(23, 59, 59, 999)

      const startMonth = startDate.getMonth()
      const endMonth = endDate.getMonth()
      const startYear = startDate.getFullYear()
      const endYear = endDate.getFullYear()

      if (startMonth !== endMonth || startYear !== endYear) {
        const monthKeys: string[] = []
        const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1)

        const includeYearInLabel = startYear !== endYear

        while (currentDate.getTime() <= endDate.getTime()) {
          const year = currentDate.getFullYear()
          const month = currentDate.getMonth() + 1
          const monthKey = `${year}-${month.toString().padStart(2, '0')}`

          if (!monthKeys.includes(monthKey)) {
            monthKeys.push(monthKey)

            let label = MONTH_ABBREVIATIONS[currentDate.getMonth()]

            if (includeYearInLabel) {
              label += ` '${String(year).slice(-2)}`
            }

            labels.push(label)
          }

          currentDate.setMonth(currentDate.getMonth() + 1)
        }

        getGroupLabel = (date: Date): string | null => {
          const monthKey = date.toISOString().substring(0, 7)

          if (normalizeDate(date).getTime() >= normalizeDate(startDate).getTime() && normalizeDate(date).getTime() <= normalizeDate(endDate).getTime()) {

            if (monthKeys.includes(monthKey)) {
              let label = MONTH_ABBREVIATIONS[date.getMonth()]
              if (includeYearInLabel) {
                label += ` '${String(date.getFullYear()).slice(-2)}`
              }

              return label
            }
          }

          return null
        }
      } else {
        const dateToLabelMap: Record<string, string> = {}

        const msPerDay = 24 * 60 * 60 * 1000
        const timeDiff = endDate.getTime() - startDate.getTime()
        const numDays = Math.floor(timeDiff / msPerDay) + 1

        for (let i = 0; i < numDays; i++) {
          const currentDate = new Date(startDate)
          currentDate.setDate(startDate.getDate() + i)

          const dateKey = currentDate.toISOString().split('T')[0]
          const label = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`

          labels.push(label)
          dateToLabelMap[dateKey] = label
        }

        getGroupLabel = (date: Date): string | null => {
          return dateToLabelMap[normalizeDate(date).toISOString().split('T')[0]] || null
        }
      }

      break
    }
    case '1w': {
      const sevenDaysAgo = new Date(today)
      sevenDaysAgo.setDate(today.getDate() - 6)
      const dateToLabelMap: Record<string, string> = {}

      for (let i = 0; i < 7; i++) {
        const d = new Date(sevenDaysAgo)
        d.setDate(sevenDaysAgo.getDate() + i)
        const label = `${d.getMonth() + 1}/${d.getDate()}`
        const dateKey = d.toISOString().split('T')[0]
        labels.push(label)
        dateToLabelMap[dateKey] = label
      }

      getGroupLabel = (date: Date): string | null => {
        return dateToLabelMap[normalizeDate(date).toISOString().split('T')[0]] || null
      }
      break
    }

    case '4w': {
      labels = ['WEEK 1', 'WEEK 2', 'WEEK 3', 'WEEK 4']
      const rangeStart = new Date(today)
      rangeStart.setDate(today.getDate() - 28)
      normalizeDate(rangeStart)

      getGroupLabel = (date: Date): string | null => {
        const diffDays = Math.floor((normalizeDate(date).getTime() - rangeStart.getTime()) / msPerDay)
        if (diffDays >= 0 && diffDays < 28) {
          const weekIndex = Math.floor(diffDays / 7)

          return labels[weekIndex]
        }

        return null
      }
      break
    }

    case '3m': {
      const monthKeys: string[] = []

      for (let i = 2; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
        const year = d.getFullYear()
        const month = d.getMonth() + 1
        monthKeys.push(`${year}-${month.toString().padStart(2, '0')}`)
        labels.push(MONTH_ABBREVIATIONS[d.getMonth()])
      }

      getGroupLabel = (date: Date): string | null => {
        const monthKey = date.toISOString().substring(0, 7)

        return monthKeys.includes(monthKey) ? MONTH_ABBREVIATIONS[date.getMonth()] : null
      }
      break
    }

    case 'ytd': {
      const currentMonthIndex = today.getMonth()
      const monthKeys: string[] = []

      for (let i = 0; i <= currentMonthIndex; i++) {
        const month = i + 1
        monthKeys.push(`${currentYear}-${month.toString().padStart(2, '0')}`)
        labels.push(MONTH_ABBREVIATIONS[i])
      }

      getGroupLabel = (date: Date): string | null => {
        const monthKey = date.toISOString().substring(0, 7)

        return monthKeys.includes(monthKey) ? MONTH_ABBREVIATIONS[date.getMonth()] : null
      }
      break
    }

    case 'all':
    default: {
      let earliestYear: number = currentYear

      if (workouts.length > 0) {
        workouts.forEach((workout) => {
          const year = new Date(workout.data.payload.createdAt).getFullYear()
          if (year < earliestYear) {
            earliestYear = year
          }
        })

        for (let y = earliestYear; y <= currentYear; y++) {
          labels.push(y.toString())
        }
      }

      getGroupLabel = (date: Date): string => date.getFullYear().toString()
      break
    }
  }

  return { labels, getGroupLabel }
}

/**
 * Generates ChartData for a bar chart from an array of workout data,
 * grouping and labeling based on the specified time range.
 */
export function mapBarChartData(workouts: Workout[], timeRange: TimeRangeFilter['value'], units: string, dateRange?: [number, number] | null, equipmentFilter?: string): ChartData<'bar'> {

  const aggregatedData: Map<string, Map<string, number>> = new Map()
  const deviceTypes = new Set<string>()
  const { labels, getGroupLabel } = timeGrouping(workouts, timeRange, dateRange)

  workouts.forEach((workout) => {
    const startTimeDate = new Date(workout.data.payload.createdAt)
    const groupLabel = getGroupLabel(startTimeDate)

    if (!groupLabel) return

    machineDistances(workout, equipmentFilter).forEach(([deviceType, meters]) => {
      deviceTypes.add(deviceType)
      if (!aggregatedData.has(deviceType)) {
        aggregatedData.set(deviceType, new Map<string, number>())
      }
      const deviceMap = aggregatedData.get(deviceType)!
      deviceMap.set(groupLabel, (deviceMap.get(groupLabel) || 0) + inUnits(meters, units))
    })
  })

  const datasets = Array.from(deviceTypes)
    .map((deviceType, index) => {
      const dataMap = aggregatedData.get(deviceType)!
      const data = labels.map((label) => dataMap.get(label) || 0)

      return {
        label           : CUSTOM_DEVICE_MAP[deviceType]?.label || deviceType,
        data            : data,
        backgroundColor : CUSTOM_DEVICE_MAP[deviceType]?.color || `hsl(${index * 50}, 70%, 50%)`,
      }
    })
    .sort((a, b) => b.label.localeCompare(a.label))

  return {
    labels   : labels,
    datasets : datasets,
  }
}

/**
 * The Air Rhino's bar chart: the weight moved in each bucket of the time
 * range, one blue series, in the requested unit.
 */
export function mapRhinoBarChartData(workouts: Workout[], timeRange: TimeRangeFilter['value'], unit: WeightUnitsFilter['value'], dateRange?: [number, number] | null): ChartData<'bar'> {
  const { labels, getGroupLabel } = timeGrouping(workouts, timeRange, dateRange)
  const weightByLabel = new Map<string, number>()

  workouts.forEach(workout => {
    const groupLabel = getGroupLabel(new Date(workout.data.payload.createdAt))
    if (!groupLabel) return

    weightByLabel.set(groupLabel, (weightByLabel.get(groupLabel) || 0) + rhinoWeight(workout.data.performanceMetrics, unit))
  })

  return {
    labels,
    datasets: [{
      label           : AIR_RHINO.label,
      data            : labels.map(label => weightByLabel.get(label) || 0),
      backgroundColor : AIR_RHINO.color,
    }],
  }
}

export function mapPieChartData(workouts: Workout[], units: string, equipmentFilter?: string): ChartData<'pie'> {
  const deviceDistanceMap: Record<string, number> = {}

  for (const workout of workouts) {
    machineDistances(workout, equipmentFilter).forEach(([deviceType, meters]) => {
      deviceDistanceMap[deviceType] = (deviceDistanceMap[deviceType] || 0) + inUnits(meters, units)
    })
  }

  const chartDataEntries = Object.keys(deviceDistanceMap).map(deviceType => ({
    deviceType,
    label    : CUSTOM_DEVICE_MAP[deviceType]?.label || deviceType,
    color    : CUSTOM_DEVICE_MAP[deviceType]?.color || CUSTOM_DEVICE_MAP.unknown.color,
    distance : deviceDistanceMap[deviceType],
  }))

  chartDataEntries.sort((a, b) => b.label.localeCompare(a.label))

  const labels: string[] = chartDataEntries.map(entry => entry.label)
  const data: number[] = chartDataEntries.map(entry => entry.distance)
  const backgroundColors: string[] = chartDataEntries.map(entry => entry.color)

  const unitLabel = units === 'meters'
    ? 'M'
    : units === 'yards' ? 'YDS' : 'MI'

  return {
    labels   : labels,
    datasets : [{
      label           : `Total Distance (${unitLabel})`,
      data            : data,
      backgroundColor : backgroundColors,
      borderWidth     : 1,
      borderColor     : '#fff',
    }],
  }
}

export function getWorkoutsByDevice(workouts: Workout[], equipmentFilter?: string): AggregatedWorkoutData[] {
  const groupedByType: Record<string, { totalMeters: number, totalYards: number, sessionCount: number }> = {}

  for (const workout of workouts) {
    machineDistances(workout, equipmentFilter).forEach(([deviceType, meters]) => {
      if (!groupedByType[deviceType]) {
        groupedByType[deviceType] = { totalMeters: 0, totalYards: 0, sessionCount: 0 }
      }
      groupedByType[deviceType].totalMeters += meters
      groupedByType[deviceType].totalYards += meters * YARDS_PER_METER
      groupedByType[deviceType].sessionCount += 1
    })
  }

  return Object.entries(groupedByType)
    .map(([deviceType, data]) => {
      const totalDistanceMiles = data.totalYards / 1760
      const avgMetersPerSession = data.sessionCount > 0 ? data.totalMeters / data.sessionCount : 0
      const avgYardsPerSession = data.sessionCount > 0 ? data.totalYards / data.sessionCount : 0
      const avgMilesPerSession = data.sessionCount > 0 ? totalDistanceMiles / data.sessionCount : 0

      return {
        type                : deviceType,
        formattedType       : CUSTOM_DEVICE_MAP[deviceType]?.label || deviceType,
        distanceMeters      : Math.round(data.totalMeters).toLocaleString(),
        distanceMiles       : parseFloat(totalDistanceMiles.toFixed(2)).toLocaleString(),
        distanceYards       : Math.round(data.totalYards).toLocaleString(),
        avgMetersPerSession : Math.round(avgMetersPerSession).toLocaleString(),
        avgMilesPerSession  : parseFloat(avgMilesPerSession.toFixed(2)).toLocaleString(),
        avgYardsPerSession  : Math.round(avgYardsPerSession).toLocaleString(),
      }
    })
    .sort((a, b) => a.formattedType.localeCompare(b.formattedType))
}

export function getStatsByYear(workouts: Workout[], units: string, equipmentFilter?: string): { year: number, distance: string }[] {
  const statsByYear = new Map<number, number>()

  workouts.forEach(workout => {
    if (!workout.data.performanceMetrics) return
    const startTime = workout.data.payload.createdAt
    const year = new Date(startTime).getFullYear()
    const meters = workoutShare(workout, equipmentFilter || 'all').distance
    const distance: number = units === 'meters' ? meters : meters * YARDS_PER_METER
    const currentTotal = statsByYear.get(year) || 0
    statsByYear.set(year, currentTotal + distance)
  })

  return Array.from(statsByYear.entries()).map(([year, totalDistance]) => ({
    year     : year,
    distance : units === 'meters'
      ? `${Math.round(totalDistance).toLocaleString()}M`
      : units === 'yards'
        ? `${Math.round(totalDistance).toLocaleString()} yds`
        : `${parseFloat((totalDistance / 1760).toFixed(2)).toLocaleString()} mi`,
  }))
}

export function formatElapsedTime(time: number): string {
  const totalSeconds = Math.floor(time / 1000)
  const seconds = totalSeconds % 60
  const totalMinutes = Math.floor(totalSeconds / 60)
  const minutes = totalMinutes % 60
  const hours = Math.floor(totalMinutes / 60)

  const pad = (num: number): string => String(num).padStart(2, '0')

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}